import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  getMenu,
  getMenus,
  getSubmenus,
  createMenu,
  createSubmenu,
  updateMenu,
  updateMenuSeq,
  updateSubmenuSeq,
} from '../api/menus.api';
import {
  IMenu,
  IGetMenu,
  IGetMenus,
  IMenuReq,
  IMenuSeqReq,
} from '../models/menus.model';

export interface IMenuState {
  menu?: IMenu;
  menus?: IMenu[];
  submenus?: IMenu[];
  isLoading: boolean;
  error?: AxiosError | string;
  selectMenu: {
    mainMenu: {
      info?: IMenu
      selectedKeys: string[]
    },
    subMenu?: {
      info?: IMenu,
      selectedKeys?: string[]
    }
  } | null
}

const initialState: IMenuState = {
  menu: undefined,
  menus: [],
  submenus: [],
  isLoading: false,
  selectMenu: null,
};

const menuSlicer = createSlice({
  name: 'menu',
  initialState,
  reducers: {
    getMenuStart: (state: IMenuState) => {
      state.menu = undefined;
      state.isLoading = true;
      state.error = undefined;
    },
    getMenuSuccess: (state: IMenuState, action: PayloadAction<IGetMenu>) => {
      state.menu = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getMenuFailure: (
      state: IMenuState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.menu = undefined;
      state.isLoading = false;
      state.error = action.payload;
    },
    getMenusStart: (state: IMenuState) => {
      state.menus = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getMenusSuccess: (state: IMenuState, action: PayloadAction<IGetMenus>) => {
      state.menus = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getMenusFailure: (
      state: IMenuState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.menus = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    getSubmenusStart: (state: IMenuState) => {
      state.submenus = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getSubmenusSuccess: (
      state: IMenuState,
      action: PayloadAction<IGetMenus>,
    ) => {
      state.submenus = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getSubmenusFailure: (
      state: IMenuState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.submenus = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    requestStart: (state: IMenuState) => {
      state.isLoading = true;
      state.error = undefined;
    },
    requestFailure: (
      state: IMenuState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    createSuccess: (state: IMenuState) => {
      state.isLoading = false;
      state.error = undefined;
    },
    updateSuccess: (state: IMenuState) => {
      state.isLoading = false;
      state.error = undefined;
    },
    changeSelectMenu: (state, action: any) => {
      state.selectMenu = action.payload;
    }
  },
});

export const {
  getMenuStart,
  getMenuSuccess,
  getMenuFailure,
  getMenusStart,
  getMenusSuccess,
  getMenusFailure,
  getSubmenusStart,
  getSubmenusSuccess,
  getSubmenusFailure,
  requestStart,
  requestFailure,
  createSuccess,
  updateSuccess,
  changeSelectMenu
} = menuSlicer.actions;

export default menuSlicer.reducer;

export const doGetMenu =
  (id: number): any =>
  async (dispatch: any) => {
    try {
      dispatch(getMenuStart());
      const response = await getMenu(id);
      dispatch(getMenuSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getMenuFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doGetMenus = (): any => async (dispatch: any) => {
  try {
    dispatch(getMenusStart());
    const response = await getMenus();
    dispatch(getMenusSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getMenusFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doGetSubmenus =
  (id: number): any =>
  async (dispatch: any) => {
    try {
      dispatch(getSubmenusStart());
      const response = await getSubmenus(id);
      dispatch(getSubmenusSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getSubmenusFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doCreateMenu =
  (data: IMenuReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await createMenu(data);
      dispatch(createSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doCreateSubmenu =
  (id: number, data: IMenuReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await createSubmenu(id, data);
      dispatch(createSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateMenu =
  (id: number, data: IMenuReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateMenu(id, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateMenuSeq =
  (data: IMenuSeqReq[]): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateMenuSeq(data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateSubmenuSeq =
  (id: number, data: IMenuSeqReq[]): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateSubmenuSeq(id, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };
